import { ArrowLeft, ClipboardList } from "lucide-react";
import { useMemo, useState } from "react";

import {
  formatRate,
  RATE_TABLE_COLUMNS,
  SEASON_TABLE_COLUMNS,
  type ArchivedGameStats,
  type SeasonSummary,
} from "../../domain/baseballStats";
import type { SeasonModel } from "../../shared/types/contracts";
import { Button } from "@/shared/ui/button";
import { ButtonGroup } from "@/shared/ui/button-group";
import { Card, CardContent } from "@/shared/ui/card";
import { PageHeader } from "@/shared/ui/page-header";
import {
  nextSortState,
  SortableTableHead,
  sortRows,
  type SortState,
} from "@/shared/ui/sortable-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";

interface SeasonScreenProps {
  summary?: SeasonSummary;
  games?: ArchivedGameStats[];
  regularSummary?: SeasonSummary;
  playoffSummary?: SeasonSummary;
  regularGames?: ArchivedGameStats[];
  playoffGames?: ArchivedGameStats[];
  seasons?: SeasonModel[];
  activeSeasonId?: string | null;
  hasActiveGame?: boolean;
  selectedSeasonId?: string;
  onSelectSeason?: (seasonId: string) => void;
  onShowBoxScore: () => void;
  onShowGame: () => void;
}

const STAT_TITLES: Record<string, string> = {
  GP: "Games played",
  AB: "At bats",
  H: "Hits",
  "1B": "Singles",
  "2B": "Doubles",
  "3B": "Triples",
  HR: "Home runs",
  BB: "Walks",
  K: "Strikeouts",
  RBI: "Runs batted in",
  R: "Runs",
  AVG: "Batting average",
  OBP: "On-base percentage",
  SLG: "Slugging percentage",
  OPS: "On-base plus slugging",
  ISO: "Isolated power",
};

type SeasonSortKey =
  | "GP"
  | (typeof SEASON_TABLE_COLUMNS)[number]
  | (typeof RATE_TABLE_COLUMNS)[number];

const DEFAULT_SORT: SortState<SeasonSortKey> = {
  key: "OPS",
  direction: "desc",
};

export function SeasonScreen({
  summary,
  games,
  regularSummary,
  playoffSummary,
  regularGames,
  playoffGames,
  seasons = [],
  activeSeasonId = null,
  hasActiveGame = true,
  selectedSeasonId = activeSeasonId ?? "all",
  onSelectSeason = () => undefined,
  onShowBoxScore,
  onShowGame,
}: SeasonScreenProps) {
  const [competition, setCompetition] = useState<"regular" | "playoffs">(
    "regular",
  );
  const [sortState, setSortState] = useState(DEFAULT_SORT);
  const selectedLabel =
    selectedSeasonId === "all"
      ? "All Time"
      : (seasons.find((season) => season.id === selectedSeasonId)?.label ??
        selectedSeasonId);
  const emptySummary: SeasonSummary = { wins: 0, losses: 0, ties: 0, rows: [] };
  const selectedSummary =
    competition === "playoffs"
      ? (playoffSummary ?? emptySummary)
      : (regularSummary ?? summary ?? emptySummary);
  const selectedGames =
    competition === "playoffs"
      ? (playoffGames ?? [])
      : (regularGames ?? games ?? []);
  const { wins: w, losses: l, ties: t, rows } = selectedSummary;
  const sortedRows = useMemo(
    () => sortRows(rows, sortState, seasonSortValue),
    [rows, sortState],
  );
  const onSort = (key: SeasonSortKey) =>
    setSortState((current) => nextSortState(current, key));

  return (
    <div className="mx-auto w-full max-w-5xl space-y-3">
      <PageHeader
        title="STATS"
        eyebrow={selectedLabel}
        actions={
          hasActiveGame ? (
            <>
              <Button
                type="button"
                size="xs"
                variant="outline"
                onClick={onShowBoxScore}
              >
                <ClipboardList aria-hidden="true" />
                BOX SCORE
              </Button>
              <Button
                type="button"
                size="xs"
                variant="outline"
                aria-label="← GAME"
                onClick={onShowGame}
              >
                <ArrowLeft aria-hidden="true" />
                GAME
              </Button>
            </>
          ) : null
        }
      />

      <Card>
        <CardContent className="pt-4">
          <label className="grid gap-1 text-sm font-bold">
            STAT RANGE
            <select
              aria-label="Stat range"
              value={selectedSeasonId}
              onChange={(event) => onSelectSeason(event.target.value)}
              className="h-10 rounded-[var(--radius-sm)] border border-input bg-surface px-3 text-sm text-foreground"
            >
              <option value="all">All Time</option>
              {seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.label}
                  {season.id === activeSeasonId ? " (active)" : ""}
                </option>
              ))}
            </select>
          </label>
        </CardContent>
      </Card>

      <ButtonGroup className="w-full" aria-label="Statistics competition">
        <Button
          type="button"
          size="sm"
          variant={competition === "regular" ? "selected" : "outline"}
          aria-pressed={competition === "regular"}
          className="flex-1"
          onClick={() => setCompetition("regular")}
        >
          REGULAR SEASON
        </Button>
        <Button
          type="button"
          size="sm"
          variant={competition === "playoffs" ? "selected" : "outline"}
          aria-pressed={competition === "playoffs"}
          className="flex-1"
          onClick={() => setCompetition("playoffs")}
        >
          PLAYOFFS
        </Button>
      </ButtonGroup>

      <section className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-[var(--radius-sm)] border border-border bg-surface/65 px-3 py-2">
        <strong className="font-mono text-base tracking-[0.08em] text-warning">
          RECORD: {w}–{l}
          {t ? `–${t}` : ""}
        </strong>
        <span className="text-xs text-foreground-muted">
          {competition === "playoffs"
            ? `(${selectedGames.length} playoff games archived)`
            : `(${selectedGames.length} games archived)`}
        </span>
      </section>

      {rows.length === 0 ? (
        <p className="rounded-[var(--radius-sm)] border border-dashed border-border px-3 py-4 text-sm text-foreground-muted">
          {competition === "playoffs" ? (
            <>No archived playoff games in {selectedLabel}.</>
          ) : (
            <>No archived regular-season games in {selectedLabel}.</>
          )}
        </p>
      ) : (
        <Table
          aria-label={
            competition === "playoffs"
              ? "Playoff batting statistics"
              : "Season batting statistics"
          }
        >
          <TableHeader>
            <TableRow className="border-border hover:bg-surface-raised">
              <TableHead
                scope="col"
                className="sticky left-0 z-10 min-w-32 bg-surface-raised text-left"
              >
                PLAYER
              </TableHead>
              <SortableTableHead
                sortKey="GP"
                sortState={sortState}
                onSort={onSort}
              >
                <StatHeading value="GP" />
              </SortableTableHead>
              {SEASON_TABLE_COLUMNS.map((heading) => (
                <SortableTableHead
                  key={heading}
                  sortKey={heading}
                  sortState={sortState}
                  onSort={onSort}
                >
                  <StatHeading value={heading} />
                </SortableTableHead>
              ))}
              {RATE_TABLE_COLUMNS.map((heading) => (
                <SortableTableHead
                  key={heading}
                  sortKey={heading}
                  sortState={sortState}
                  onSort={onSort}
                  className={heading === "OPS" ? "bg-warning/10" : undefined}
                >
                  <StatHeading value={heading} />
                </SortableTableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedRows.map((row) => (
              <TableRow key={row.id ?? row.name}>
                <TableCell
                  title={row.name}
                  className="sticky left-0 z-10 max-w-40 overflow-hidden bg-surface text-left font-sans text-sm font-bold text-ellipsis"
                >
                  {row.name}
                </TableCell>
                <TableCell>{row.s.GP}</TableCell>
                {SEASON_TABLE_COLUMNS.map((column) => (
                  <TableCell key={column}>{row.s[column]}</TableCell>
                ))}
                <TableCell>{formatRate(row.avg)}</TableCell>
                <TableCell>{formatRate(row.slg)}</TableCell>
                <TableCell className="bg-warning/10 font-bold text-warning">
                  {formatRate(row.ops)}
                </TableCell>
                <TableCell>{formatRate(row.obp)}</TableCell>
                <TableCell>{formatRate(row.iso)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function StatHeading({ value }: { value: string }) {
  return (
    <abbr
      title={STAT_TITLES[value]}
      className="cursor-help decoration-dotted underline-offset-2"
    >
      {value}
    </abbr>
  );
}

function seasonSortValue(
  row: SeasonSummary["rows"][number],
  key: SeasonSortKey,
): number | undefined {
  switch (key) {
    case "GP":
      return row.s.GP;
    case "AVG":
      return row.avg;
    case "OBP":
      return row.obp;
    case "SLG":
      return row.slg;
    case "OPS":
      return row.ops;
    case "ISO":
      return row.iso;
    default:
      return row.s[key as keyof typeof row.s];
  }
}
